import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Button } from 'components/Button/Button';
import { PasswordRequirementsAndMatch } from 'components/PasswordRequirementsAndMatch/PasswordRequirementsAndMatch';
import { TextInput } from 'components/TextInput/TextInput';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { z } from 'zod';
import { useResetPasswordMutation } from '../api/auth-api';
import ResetTwoFactor from './components/ResetTwoFactor';
import messages from './messages';
import { ResetPasswordState } from './types/ResetPasswordState';

export const Route = createFileRoute('/auth/reset-password/')({
  component: ResetPassword,
  validateSearch: zodValidator(
    z.object({
      token: z.string(),
      '2fa': z.boolean().default(false),
    }),
  ),
});

export default function ResetPassword() {
  const [state, setState] = React.useState<ResetPasswordState>({
    stepNumber: 1,
  });
  const [resetPassword, resetPasswordData] = useResetPasswordMutation();

  const { token: emailToken, '2fa': tfaEnabled } = Route.useSearch();
  const navigate = useNavigate();

  const { language } = useSelector(
    (rootState: RootState) => rootState.language,
  );

  const resetPasswordMessages = messages[language].resetPassword;

  function checkPassword() {
    // True otherwise will always display warning even when the page is just loading in
    if (!state.newPassword) return true;
    if (state.newPassword?.length < 6) return false;
    if (state.newPassword?.length > 64) return false;
    if (!/[a-z]/.test(state.newPassword)) return false;
    if (!/[A-Z]/.test(state.newPassword)) return false;
    if (!/[0-9]/.test(state.newPassword)) return false;

    return true;
  }

  function checkMismatchPassword() {
    if (!state.newPassword) return true;
    if (!state.confirmNewPassword) return true;
    return state.newPassword === state.confirmNewPassword;
  }

  function isStateValid(): boolean {
    if (!state.newPassword) return false;
    if (!state.confirmNewPassword) return false;
    if (!checkMismatchPassword()) return false;
    if (!checkPassword()) return false;

    return true;
  }

  const onContinuePress = async (tfaPin: string) => {
    const res = await resetPassword({
      credential: emailToken,
      newPassword: state.newPassword!,
      totp: tfaPin ?? undefined,
    });

    if (!res.error) {
      navigate({ from: Route.fullPath, to: './completed' });
    }
  };

  function twoFaCheck(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!isStateValid()) return;

    if (tfaEnabled) {
      let { stepNumber } = state;

      setState({
        ...state,
        stepNumber: ++stepNumber,
        twoFactorToken: emailToken,
      });
    } else {
      onContinuePress('');
    }
  }

  function renderActiveStep() {
    switch (state.stepNumber) {
      case 1:
      default:
        return (
          <div className="bg-ghost-white py-2">
            <form onSubmit={twoFaCheck}>
              <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="font-semibold leading-9 mt-6 text-2xl text-black text-center">
                  {resetPasswordMessages.heading}
                </h2>
                <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-widest">
                  {resetPasswordMessages.subHeading}
                </p>
              </div>
              <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="m-6">
                  <TextInput
                    name="password"
                    type="password"
                    label={resetPasswordMessages.passwordLabel}
                    onChange={value => {
                      setState({
                        ...state,
                        newPassword: (value as string) || '',
                      });
                    }}
                    warning={!checkPassword()}
                    z-10
                  />
                  <TextInput
                    name="confirmPassword"
                    type="password"
                    label={resetPasswordMessages.confirmPasswordLabel}
                    onChange={value => {
                      setState({
                        ...state,
                        confirmNewPassword: (value as string) || '',
                      });
                    }}
                    warning={!checkMismatchPassword()}
                  />
                  {resetPasswordData.error && (
                    <div className="text-error text-sm">
                      {resetPasswordData.error.message}
                    </div>
                  )}
                  <PasswordRequirementsAndMatch
                    isPasswordMatch={true}
                    passwordState={state}
                  />
                  <div className="mt-6">
                    <span className="block w-full rounded-md">
                      <Button
                        onClick={twoFaCheck}
                        disabled={!isStateValid()}
                        loading={resetPasswordData.isLoading}
                        w-full
                        flex
                        justify-center
                        py-2
                        px-4
                        border
                        border-transparent
                        text-sm
                        font-normal
                        rounded-md
                        text-white
                        transition
                        duration-150
                        ease-in-out
                        bg-vivid-blue
                        type="submit">
                        {!resetPasswordData.isLoading &&
                          resetPasswordMessages.continueButton}
                      </Button>
                    </span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        );
      case 2:
        return <ResetTwoFactor onTwoFactorEntered={onContinuePress} />;
    }
  }

  return renderActiveStep();
}
